import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  colors,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import L from "leaflet";
import { MapContainer, TileLayer, Polyline, Tooltip, Marker, Pane } from "react-leaflet";
import { useTranslation } from "../i18n/useTranslation";
import { getZoneTypeVisual } from "../utils/zoneTypeVisuals";

const mapCenter = [33.4, 44.4];
const trainArrowSvgMarkup = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 2c-.45 0-.86.24-1.08.62l-6 10a1.25 1.25 0 0 0 1.08 1.88H11v6.75c0 .69.56 1.25 1.25 1.25h.5c.69 0 1.25-.56 1.25-1.25V14.5h4.99a1.25 1.25 0 0 0 1.08-1.88l-6-10A1.25 1.25 0 0 0 12 2z" />
  </svg>
`.trim();

const TILE_LAYERS = {
  transport: {
    url: "https://tile.memomaps.de/tilegen/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors & <a href="https://memomaps.de/">MEMO</a>',
  },
  standard: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
};

const isFiniteCoordinate = (value) => Number.isFinite(value);

const NetworkMap = ({
  routes,
  allRoutes = [],
  trains = [],
  mapLayer,
  onMapLayerChange,
  drawLines,
  onDrawLinesChange,
  selectedRouteIds = [],
  onToggleRoute,
  onClearRoutes,
  stationTrainCounts = new Map(),
}) => {
  const { t, translateRouteName } = useTranslation();
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const rightAnchorStyle = useMemo(
    () => ({
      right: 16,
      left: "auto",
    }),
    []
  );

  const openFullScreen = () => setIsFullScreenOpen(true);
  const closeFullScreen = () => setIsFullScreenOpen(false);

  const handleMapCreated = useCallback((map) => {
    const ensurePane = (name, zIndex) => {
      const existingPane = map.getPane(name);
      const pane = existingPane || map.createPane(name);
      if (pane) {
        pane.style.zIndex = String(zIndex);
        pane.style.pointerEvents = "auto";
      }
    };

    ensurePane("stationsPane", 650);
    ensurePane("trainsPane", 700);
  }, []);

  const routeColorMap = useMemo(() => {
    const map = new Map();
    routes.forEach((route) => {
      map.set(route.id, route.color);
    });
    return map;
  }, [routes]);

  const getStationCount = useMemo(() => {
    if (!stationTrainCounts) {
      return () => 0;
    }
    if (typeof stationTrainCounts.get === "function") {
      return (id) => stationTrainCounts.get(id) || 0;
    }
    return (id) => stationTrainCounts[id] || 0;
  }, [stationTrainCounts]);

  const polylineLayers = useMemo(() => {
    if (!drawLines) {
      return null;
    }

    return routes.map((route) => (
      <Polyline
        key={`polyline-${route.id}`}
        positions={route.stations.map((station) => [station.lat, station.lng])}
        pathOptions={{ color: route.color, weight: 4, opacity: 0.8 }}
      />
    ));
  }, [routes, drawLines]);

  const stationMarkers = useMemo(() => {
    return routes.flatMap((route) =>
      route.stations.map((station, stationIndex) => {
        const count = getStationCount(station.id);
        const icon = L.divIcon({
          className: "",
          iconAnchor: [10, 10],
          iconSize: [20, 20],
          html: `
            <div class="station-marker" style="--station-color: ${route.color}">
              <span class="station-marker__count">${count}</span>
            </div>
          `,
        });

        return (
          <Marker
            key={`station-${route.id}-${station.id}-${stationIndex}`}
            position={[station.lat, station.lng]}
            icon={icon}
            zIndexOffset={0}
            pane="stationsPane"
          >
            <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {station.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {translateRouteName(route.id, route.displayName || route.name)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("map.stationCount", { count })}
                </Typography>
              </Stack>
            </Tooltip>
          </Marker>
        );
      })
    );
  }, [routes, getStationCount, t, translateRouteName]);

  const trainMarkers = useMemo(() => {
    return trains
          .filter(
            (train) =>
              isFiniteCoordinate(train.lat) && isFiniteCoordinate(train.lng) && train.type
          )
          .map((train) => {
            const routeColor =
              train.routeColor || routeColorMap.get(train.routeId) || train.type.color;

            const rotation = Number.isFinite(train.course)
              ? ((train.course % 360) + 360) % 360
              : 0;

            const icon = L.divIcon({
              className: "train-marker-icon leaflet-div-icon",
              iconAnchor: [14, 14],
              iconSize: [28, 28],
              html: `
                <div class="train-marker${
                  train.isInsideKnownGeofence ? "" : " train-marker--pulsing"
                }${train.isOffline ? " train-marker--offline" : ""}"
                  style="--train-border-color: ${routeColor}; --train-arrow-color: ${
                    train.isOffline ? "#facc15" : "#22c55e"
                  }">
                  <span class="train-marker__arrow" style="transform: rotate(${rotation}deg);">${trainArrowSvgMarkup}</span>
                </div>
              `,
            });

            const geofencesToDisplay = Array.isArray(train.geofences)
              ? train.geofences.filter((zone) => !zone?.isStation && zone?.name)
              : [];
            const showGeofences =
              !train.isInsideKnownGeofence && geofencesToDisplay.length > 0;

            const tooltipLocationContent = train.isInsideKnownGeofence ? (
              <Typography variant="caption" color="text.secondary">
                {train.stationName}
              </Typography>
            ) : showGeofences ? (
              <Stack spacing={0.5} alignItems="flex-start">
                {geofencesToDisplay.map((zone, index) => {
                  const { Icon, color } = getZoneTypeVisual(zone.type);
                  const key = zone.id ?? `${zone.name}-${index}`;
                  return (
                    <Stack
                      key={key}
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                    >
                      <Icon sx={{ fontSize: 14, color }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        {zone.name}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t("train.unassignedRoute")}
              </Typography>
            );

            return (
              <Marker
                key={train.id}
                position={[train.lat, train.lng]}
                icon={icon}
                zIndexOffset={1000}
                pane="trainsPane"
              >
                <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {train.code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {train.routeName}
                    </Typography>
                    {tooltipLocationContent}
                  </Stack>
                </Tooltip>
              </Marker>
            );
          });
  }, [trains, routeColorMap, t]);

  const renderLayerToggle = (boxSx = {}, boxStyle) => (
    <Box
      sx={{
        bgcolor: "rgba(255,255,255,0.92)",
        borderRadius: 999,
        boxShadow: "0 6px 20px rgba(15,23,42,0.15)",
        pointerEvents: "auto",
        display: "inline-flex",
        ...boxSx,
      }}
      style={boxStyle}
    >
      <ToggleButtonGroup
        exclusive
        value={mapLayer}
        onChange={(_, value) => value && onMapLayerChange(value)}
        size="small"
        sx={{
          p: 0.5,
          "& .MuiToggleButton-root": {
            border: 0,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 600,
            px: 1.5,
          },
        }}
      >
        <ToggleButton value="transport">{t("map.transport")}</ToggleButton>
        <ToggleButton value="standard">{t("map.standard")}</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const renderFiltersOverlay = () => (
    <Box
      sx={{
        position: "absolute",
        top: 16,
        width: { xs: "80%", sm: 320 },
        maxHeight: "90%",
        pointerEvents: "auto",
        opacity: 0.82,
        transition: "opacity 0.3s ease",
        zIndex: (theme) => theme.zIndex.tooltip + 1,
        "&:hover": { opacity: 1 },
      }}
      style={rightAnchorStyle}
    >
      <Paper
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(6px)",
          maxHeight: "100%",
          overflowY: "auto",
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t("map.filters")}
            </Typography>
            <Button size="small" onClick={onClearRoutes}>
              {t("map.allRoutes")}
            </Button>
          </Stack>

          {renderLayerToggle({ alignSelf: "flex-end" })}

          <Stack spacing={1}>
            {allRoutes.map((route) => {
              const localizedName = translateRouteName(
                route.id,
                route.displayName || route.name
              );
              const checked =
                !selectedRouteIds.length || selectedRouteIds.includes(route.id);
              return (
                <FormControlLabel
                  key={route.id}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={() => onToggleRoute(route.id)}
                      sx={{ color: route.color, "&.Mui-checked": { color: route.color } }}
                    />
                  }
                  label={localizedName}
                />
              );
            })}
          </Stack>

          <Divider />

          <FormControlLabel
            control={
              <Checkbox
                checked={drawLines}
                onChange={(event) => onDrawLinesChange(event.target.checked)}
                color="primary"
              />
            }
            label={t("map.drawLines")}
          />
        </Stack>
      </Paper>
    </Box>
  );

  const tileLayer = TILE_LAYERS[mapLayer] || TILE_LAYERS.transport;

  const renderMap = (height, { withOverlay = false, showLayerToggle = !withOverlay } = {}) => (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      <MapContainer
        center={mapCenter}
        zoom={6}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
        key={height}
        whenCreated={handleMapCreated}
      >
        <Pane name="stationsPane" style={{ zIndex: 650, pointerEvents: "auto" }} />
        <Pane name="trainsPane" style={{ zIndex: 700, pointerEvents: "auto" }} />
        <TileLayer url={tileLayer.url} attribution={tileLayer.attribution} />
        {polylineLayers}
        {stationMarkers}
        {trainMarkers}
      </MapContainer>

      {showLayerToggle &&
        renderLayerToggle(
          {
            position: "absolute",
            top: 16,
            zIndex: (theme) => theme.zIndex.tooltip + 1,
          },
          rightAnchorStyle
        )}

      {withOverlay && renderFiltersOverlay()}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid rgba(15,23,42,0.08)",
      }}
    >
      <Stack spacing={0}>
        <Box sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Interactive corridor view
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pan and zoom to explore the relative placement of each station along the network
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FullscreenIcon />}
              onClick={openFullScreen}
            >
              Full screen
            </Button>
          </Stack>
        </Box>
        <Box sx={{ height: 420 }}>
          {renderMap(420, { showLayerToggle: !isFullScreenOpen })}
        </Box>
      </Stack>
      <Dialog fullScreen open={isFullScreenOpen} onClose={closeFullScreen}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1, borderBottom: "1px solid rgba(15,23,42,0.08)" }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Network map
          </Typography>
          <IconButton color="inherit" onClick={closeFullScreen}>
            <CloseFullscreenIcon />
          </IconButton>
        </Stack>
        <DialogContent sx={{ p: 0, flex: 1 }}>
          <Box sx={{ height: "100vh", width: "100%" }}>
            {renderMap("fullscreen", { withOverlay: true })}
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default NetworkMap;
